// =====================================================
// DASHBOARD STATS — Drill-down des indicateurs globaux (issue #228)
// =====================================================
// Les 6 cartes KPI doivent être cliquables pour « consulter le détail ». Trois
// d'entre elles (BPI, Dérogations, PI en attente) n'avaient aucune destination :
// il n'existe pas de liste CRM dont le périmètre égale celui de la carte.
//
// ⚠️ Pourquoi un drill-down dédié plutôt qu'un simple lien vers une liste :
// mesuré en prod le 2026-08-26, « PI en attente » = 999 lignes (4L Trophy 2027)
// alors que la liste /prospects n'en montre que 491 — `prospects_view` exclut les
// BPI payés (`payment_status.neq.paid` dans useProspects) et la page masque les
// expirés par défaut. Router la carte vers cette liste aurait affiché la moitié
// du chiffre annoncé. On requête donc les MÊMES prédicats que compute.ts, ce qui
// rend l'écart carte ↔ détail impossible par construction.
//
// Ce module est PUR (aucune dépendance React/Supabase) → 100% testable : il ne
// décrit que « quoi requêter » ; l'exécution vit dans useKpiDetail.

import type { DateWindow, KpiKey } from './types';
import { ID_CREW_STATUSES, PI_CREW_STATUSES } from './nomenclature';
import type { FilterCondition } from '../../types/filters';

/** Table/vue interrogée pour détailler une carte. */
export type KpiDetailSource = 'payments' | 'crews' | 'contacts' | 'form_submissions';

/**
 * Descripteur de requête de détail — miroir exact des prédicats de compute.ts.
 * Les bornes sont des INSTANTS ABSOLUS (toISOString) et non des dates nues : la
 * fenêtre du dashboard est locale ([00:00:00, 23:59:59.999] Europe/Paris) et
 * `inWindow` compare des timestamps. Envoyer 'yyyy-mm-dd' au serveur décalerait
 * les bornes du fuseau de la session Postgres (UTC) → écart de comptage.
 */
export interface KpiDetailQuery {
  key: KpiKey;
  source: KpiDetailSource;
  /** product_id de l'édition N. `null` = KPI global (PR, cf. nomenclature FLAG 3). */
  editionId: string | null;
  startIso: string;
  endIso: string;
  /** Statuts crew retenus (PI / ID). Absent = pas de contrainte de statut. */
  crewStatuses?: string[];
  /** Dérogations en attente : `derogation_pending = true`. */
  derogationPendingOnly?: boolean;
  /** Dérogations acceptées : `has_derogation = true` (split réunion 22/07). */
  hasDerogationOnly?: boolean;
  /**
   * Soumissions BPI non converties (`converted_at IS NULL`) — carte « BPI en
   * attente ». Absent = toutes les soumissions BPI (carte « BPI », 22/07).
   */
  unconvertedOnly?: boolean;
}

/** Plafond de lignes ramenées — le total réel vient du `count: 'exact'`. */
export const KPI_DETAIL_LIMIT = 500;

/** Contexte de drill-down = ce que le dashboard a sous la main au clic. */
export interface KpiDetailContext {
  editionId: string | null;
  window: DateWindow;
}

/**
 * Construit le descripteur de requête d'une carte. Retourne `null` quand le
 * contexte est incomplet (pas d'édition sélectionnée pour un KPI scopé édition).
 */
export function buildKpiDetailQuery(key: KpiKey, ctx: KpiDetailContext): KpiDetailQuery | null {
  const startIso = ctx.window.start.toISOString();
  const endIso = ctx.window.end.toISOString();

  // PR est le seul KPI non scopé édition (nomenclature FLAG 3).
  if (key === 'pr') {
    return { key, source: 'contacts', editionId: null, startIso, endIso };
  }

  if (!ctx.editionId) return null;
  const base = { key, editionId: ctx.editionId, startIso, endIso };

  switch (key) {
    case 'bpi':
      // Réunion 22/07 : BPI = TOUS les BPI générés (form_submissions generates_bpi),
      // payés ou non — cf. countBpi. Plus les payment_cases payés.
      return { ...base, source: 'form_submissions' };
    case 'pi':
      return { ...base, source: 'crews', crewStatuses: [...PI_CREW_STATUSES] };
    case 'id':
      return { ...base, source: 'crews', crewStatuses: [...ID_CREW_STATUSES] };
    case 'derogations':
      return { ...base, source: 'crews', derogationPendingOnly: true };
    case 'derogations_acceptees':
      return { ...base, source: 'crews', hasDerogationOnly: true };
    case 'pi_attente':
      return { ...base, source: 'form_submissions', unconvertedOnly: true };
  }
}

/**
 * Deep-link « ouvrir la liste » proposé EN PLUS du détail, quand une liste CRM
 * peut être pré-filtrée sur le même périmètre. Réservé aux cartes adossées à
 * `crews` : `/crews` sait lire `?filters=` (CrewsListPage) et sa source
 * (`crews_filter_view`) n'applique aucune exclusion cachée — vérifié en prod, la
 * vue et la table comptent le même nombre de `derogation_pending`.
 *
 * PI/ID portent leur filtre de statut via l'opérateur `in` : le lien couvre donc
 * exactement le périmètre de la carte, comme Dérogations. Sans lui, mesuré en
 * prod le 27/08/2026 sur 4L Trophy 2027 / fenêtre par défaut, le lien ouvrait
 * 202 lignes pour une carte PI à 171 (+17 bpi_payment_pending, +13 archived,
 * +1 cancellation_compliant) et pour une carte ID à… 0.
 */
export function buildCrewsListLink(key: KpiKey, ctx: KpiDetailContext): string | null {
  if (!ctx.editionId) return null;
  if (key !== 'pi' && key !== 'id' && key !== 'derogations' && key !== 'derogations_acceptees') {
    return null;
  }

  const filters: FilterCondition[] = [
    { field: 'product_id', operator: 'equals', value: ctx.editionId },
    {
      field: 'created_at',
      operator: 'between',
      value: [ctx.window.start.toISOString(), ctx.window.end.toISOString()],
    },
  ];
  if (key === 'derogations') {
    filters.unshift({ field: 'derogation_pending', operator: 'equals', value: 'true' });
  } else if (key === 'derogations_acceptees') {
    // Même colonne booléenne que compute.ts (crews.has_derogation), exposée par
    // crews_filter_view et déjà déclarée comme filtre de la liste.
    filters.unshift({ field: 'has_derogation', operator: 'equals', value: 'true' });
  } else {
    // Mêmes ensembles que compute.ts, via la MÊME constante : une évolution de
    // la nomenclature ne peut pas désynchroniser la carte et son lien.
    const statuses = key === 'pi' ? PI_CREW_STATUSES : ID_CREW_STATUSES;
    filters.unshift({ field: 'status', operator: 'in', value: [...statuses] });
  }

  return `/crews?filters=${encodeURIComponent(JSON.stringify(filters))}`;
}

/**
 * true quand le lien liste couvre EXACTEMENT le périmètre de la carte.
 * Vrai pour les quatre cartes adossées à `crews` depuis que `in` porte le filtre
 * de statut ; conservé comme point d'accroche si une future carte devait ouvrir
 * une liste volontairement plus large.
 */
export function isCrewsListLinkExact(key: KpiKey): boolean {
  return key === 'pi' || key === 'id' || key === 'derogations' || key === 'derogations_acceptees';
}

/** Ligne de détail normalisée — commune aux 4 sources. */
export interface KpiDetailRow {
  id: string;
  /** Libellé principal (nom d'équipage, nom du contact, n° de commande…). */
  primary: string;
  /** Complément (n° d'équipage, email…). */
  secondary: string | null;
  /** Statut métier (statut crew, méthode de paiement…). */
  status: string | null;
  /** Montant en centimes (BPI uniquement). */
  amountCents: number | null;
  createdAt: string;
  /** Route de la fiche liée, quand elle existe. */
  href: string | null;
}

/** Entête de la colonne « libellé principal », par source. */
export const KPI_DETAIL_COLUMN_LABELS: Record<
  KpiDetailSource,
  { primary: string; secondary: string; status: string }
> = {
  payments: { primary: 'Équipage / payeur', secondary: 'N° de commande', status: 'Moyen' },
  crews: { primary: 'Équipage', secondary: 'N° équipage', status: 'Statut' },
  contacts: { primary: 'Contact', secondary: 'Email', status: 'Statut' },
  form_submissions: { primary: 'Contact', secondary: 'Code BPI', status: 'Statut BPI' },
};
